import React, { createContext, useContext, useState, useEffect } from 'react';
import { ethers } from 'ethers';

const Web3Context = createContext();

export const useWeb3 = () => {
  const context = useContext(Web3Context);
  if (!context) {
    throw new Error('useWeb3 must be used within a Web3Provider');
  }
  return context;
};

export const Web3Provider = ({ children }) => {
  const [account, setAccount] = useState(null);
  const [balance, setBalance] = useState('0');
  const [provider, setProvider] = useState(null);
  const [signer, setSigner] = useState(null);
  const [chainId, setChainId] = useState(null);
  const [isConnected, setIsConnected] = useState(false);

  // BNB Smart Chain network configuration
  const BNB_CHAIN_ID = process.env.REACT_APP_CHAIN_ID ? `0x${parseInt(process.env.REACT_APP_CHAIN_ID).toString(16)}` : '0x61'; // 97 in decimal for testnet
  const BNB_RPC_URL = process.env.REACT_APP_RPC_URL || 'https://data-seed-prebsc-1-s1.binance.org:8545/';

  const connectWallet = async () => {
    try {
      if (typeof window.ethereum !== 'undefined') {
        // Request account access
        const accounts = await window.ethereum.request({
          method: 'eth_requestAccounts',
        });

        if (accounts.length > 0) {
          const provider = new ethers.providers.Web3Provider(window.ethereum);
          const signer = provider.getSigner();
          const network = await provider.getNetwork();

          setProvider(provider);
          setSigner(signer);
          setAccount(accounts[0]);
          setIsConnected(true);
          setChainId(network.chainId.toString());

          // Check if we're on BNB Smart Chain
          const expectedChainId = parseInt(process.env.REACT_APP_CHAIN_ID) || 97;
          if (network.chainId !== expectedChainId) {
            await switchToBNBChain();
          }

          // Get balance
          const balance = await provider.getBalance(accounts[0]);
          setBalance(ethers.utils.formatEther(balance));

          // Listen for account changes
          window.ethereum.on('accountsChanged', handleAccountsChanged);
          window.ethereum.on('chainChanged', handleChainChanged);
        }
      } else {
        alert('Please install MetaMask or another Web3 wallet to continue');
      }
    } catch (error) {
      console.error('Error connecting wallet:', error);
      alert('Failed to connect wallet. Please try again.');
    }
  };

  const switchToBNBChain = async () => {
    try {
      await window.ethereum.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: BNB_CHAIN_ID }],
      });
    } catch (switchError) {
      // This error code indicates that the chain has not been added to MetaMask
      if (switchError.code === 4902) {
        try {
          await window.ethereum.request({
            method: 'wallet_addEthereumChain',
            params: [
              {
                chainId: BNB_CHAIN_ID,
                chainName: 'BNB Smart Chain',
                nativeCurrency: {
                  name: 'BNB',
                  symbol: 'BNB',
                  decimals: 18,
                },
                rpcUrls: [BNB_RPC_URL],
                blockExplorerUrls: [process.env.REACT_APP_BLOCK_EXPLORER_URL || 'https://testnet.bscscan.com/'],
              },
            ],
          });
        } catch (addError) {
          console.error('Error adding BNB Smart Chain:', addError);
          alert('Failed to add BNB Smart Chain to wallet');
        }
      } else {
        console.error('Error switching to BNB Smart Chain:', switchError);
        alert('Failed to switch to BNB Smart Chain');
      }
    }
  };

  const handleAccountsChanged = (accounts) => {
    if (accounts.length === 0) {
      disconnectWallet();
    } else {
      setAccount(accounts[0]);
    }
  };

  const handleChainChanged = (chainId) => {
    setChainId(parseInt(chainId, 16).toString());
    const expectedChainId = parseInt(process.env.REACT_APP_CHAIN_ID) || 97;
    if (parseInt(chainId, 16) !== expectedChainId) {
      alert('Please switch to BNB Smart Chain to use this dApp');
    }
  };

  const disconnectWallet = () => {
    setAccount(null);
    setBalance('0');
    setProvider(null);
    setSigner(null);
    setChainId(null);
    setIsConnected(false);
  };

  const getBalance = async () => {
    if (provider && account) {
      try {
        const balance = await provider.getBalance(account);
        setBalance(ethers.utils.formatEther(balance));
        return ethers.utils.formatEther(balance);
      } catch (error) {
        console.error('Error getting balance:', error);
        return '0';
      }
    }
    return '0';
  };

  useEffect(() => {
    // Check if wallet is already connected
    if (typeof window.ethereum !== 'undefined') {
      window.ethereum
        .request({ method: 'eth_accounts' })
        .then((accounts) => {
          if (accounts.length > 0) {
            connectWallet();
          }
        })
        .catch(console.error);
    }
  }, []);

  const value = {
    account,
    balance,
    provider,
    signer,
    chainId,
    isConnected,
    connectWallet,
    disconnectWallet,
    getBalance,
    switchToBNBChain,
  };

  return <Web3Context.Provider value={value}>{children}</Web3Context.Provider>;
};
